'use strict'

import moment from 'moment-timezone'
import { formatTimezone } from '../utils/formatTimezone'
import {
  timestampRegExp, isoStringRegExp,
  dateTimeRegExp, americanDateTimeRegExp, russianDateTimeRegExp
} from './regExpRecipes'

/**
 * @typedef {{
 *   type: string,
 *   value: string,
 *   timezone?: string,
 *   period?:  string,
 *   isValid: boolean,
 *   moment: Moment,
 *   format: function (formatType: string, formatTimezone: string) : string,
 *   toJSON: function (): Object
 * }} TTTime
 **/

/**
 * Create a tttime object
 *
 * Example usage:
 *
 *     const time = tttime('2017-01-26')
 *     const time = tttime('2017-01-26 18:00', 'dateTime')
 *     const time = tttime('2017-01-26 18:00', 'dateTime', 'Europe/Amsterdam')
 *
 * @param {number | string | moment} value
 * @param {string | undefined} [type]   If type is not provided, the function
 *                                      will auto detect the type
 * @param {string} [timezone]
 * @param {string} [period]             One of "day", "month", "year". Only applicable
 *                                      for types `*StartOfPeriod` en `*EndOfPeriod`
 * @param {string} [description]        Optional free text label to describe
 *                                      this tttime object
 * @return {TTTime}
 */
// TODO: the constructor has too many arguments. Pass an object instead
export function tttime (value, type, timezone = moment.tz.guess(), period = undefined, description = undefined) {
  if (typeof value === 'number') {
    return tttime(String(value), type, timezone)
  }

  if (moment.isMoment(value)) {
    return tttime(value.toISOString(), 'isoString', timezone)
  }

  const def = findDefinition(type)

  if (def.hasTimezone && moment.tz.zone(timezone) === null) {
    throw new Error(`Unknown timezone "${timezone}"`)
  }

  if (def.hasPeriod && PERIODS.indexOf(period) === -1) {
    throw new Error(`Unknown period "${period}"`)
  }

  const isValid = def.isValid(value)
  const time = def.toMoment(value, timezone)

  return Object.freeze({
    value,
    type,
    timezone,
    description,
    isValid,

    format: function (outputType, outputTimezone = timezone, outputPeriod = period) {
      if (type === outputType &&
          (timezone === outputTimezone || !outputTimezone) &&
          (period === outputPeriod || !outputPeriod)) {
        return value
      }

      // TODO: test whether outputPeriod is valid when it's required

      if (!isValid) {
        return ''
      }

      return findDefinition(outputType).fromMoment(time, outputTimezone, outputPeriod)
    },

    toMoment: function () {
      return time.clone()
    },

    toJSON: function () {
      return { value, type, timezone, period, description }
    }
  })
}

function findDefinition (type) {
  if (!DEFINITIONS[type]) {
    throw new Error(`Unknown type "${type}". Choose from: ${Object.keys(DEFINITIONS).map(JSON.stringify).join(', ')}`)
  }

  return DEFINITIONS[type]
}

// periods for start of and end of types
export const PERIODS = ['day', 'month', 'year']

export const TIMEZONES = moment.tz.names()

// TODO: change to an array? would make Config easier
export const DEFINITIONS = {
  unix: {
    type: 'unix',
    category: 'unix',
    isReadonly: false,
    hasTimezone: false,
    hasPeriod: false,
    header: 'unix timestamps',
    label: () => `unix time (s)`,
    width: 110, // px
    isValid: function (value) {
      return !!(timestampRegExp.exec(value) && moment(parseInt(value)).isValid())
    },
    toMoment: function (value, timezone) {
      return moment.unix(value.length ? Number(value) : NaN)
    },
    fromMoment: function (moment, timezone) {
      return String(moment.unix())
    }
  },

  unixStartOfPeriod: {
    type: 'unixStartOfPeriod',
    category: 'unix',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: true,
    label: (timezone, period) => `start of ${period} (s)\n${formatTimezone(timezone)}`,
    width: 150, // px
    fromMoment: function (moment, timezone, period) {
      return String(moment.clone().tz(timezone).startOf(period).unix())
    }
  },

  unixEndOfPeriod: {
    type: 'unixEndOfPeriod',
    category: 'unix',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: true,
    label: (timezone, period) => `end of ${period} (s)\n${formatTimezone(timezone)}`,
    width: 150, // px
    fromMoment: function (moment, timezone, period) {
      return String(moment.clone().tz(timezone).endOf(period).unix())
    }
  },

  timestamp: {
    type: 'timestamp',
    category: 'timestamp',
    isReadonly: false,
    hasTimezone: false,
    hasPeriod: false,
    header: 'timestamps',
    label: () => `timestamp (ms)`,
    width: 150, // px
    isValid: function (value) {
      return !!(timestampRegExp.exec(value) && moment(parseInt(value)).isValid())
    },
    toMoment: function (value) {
      return moment(value.length ? Number(value) : NaN)
    },
    fromMoment: function (moment) {
      return String(moment.valueOf())
    }
  },

  timestampStartOfPeriod: {
    type: 'timestampStartOfPeriod',
    category: 'timestamp',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: true,
    label: (timezone, period) => `start of ${period} (ms)\n${formatTimezone(timezone)}`,
    width: 150, // px
    fromMoment: function (moment, timezone, period) {
      return String(moment.clone().tz(timezone).startOf(period).valueOf())
    }
  },

  timestampEndOfPeriod: {
    type: 'timestampEndOfPeriod',
    category: 'timestamp',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: true,
    label: (timezone, period) => `end of ${period} (ms)\n${formatTimezone(timezone)}`,
    width: 150, // px
    fromMoment: function (moment, timezone, period) {
      return String(moment.clone().tz(timezone).endOf(period).valueOf())
    }
  },

  isoString: {
    type: 'isoString',
    category: 'isoString',
    isReadonly: false,
    hasTimezone: false,
    hasPeriod: false,
    header: 'ISO strings',
    label: () => `ISO string`,
    width: 260, // px
    isValid: function (value) {
      return !!(isoStringRegExp.exec(value) && moment(value).isValid())
    },
    toMoment: function (value) {
      return moment(value.length ? value.trim() : NaN)
    },
    fromMoment: function (moment) {
      return moment.toISOString()
    }
  },

  isoStartOfPeriod: {
    type: 'isoStartOfPeriod',
    category: 'isoString',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: true,
    label: (timezone, period) => `start of ${period}\n${formatTimezone(timezone)}`,
    width: 260, // px
    fromMoment: function (moment, timezone, period) {
      return moment.clone().tz(timezone).startOf(period).toISOString()
    }
  },

  isoEndOfPeriod: {
    type: 'isoEndOfPeriod',
    category: 'isoString',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: true,
    label: (timezone, period) => `end of ${period}\n${formatTimezone(timezone)}`,
    width: 260, // px
    fromMoment: function (moment, timezone, period) {
      return moment.clone().tz(timezone).endOf(period).toISOString()
    }
  },

  dateTime: {
    type: 'dateTime',
    category: 'human',
    isReadonly: false,
    hasTimezone: true,
    hasPeriod: false,
    header: 'Human date times',
    label: (timezone) => `date time\n${formatTimezone(timezone)}`,
    width: 200, // px
    isValid: function (value) {
      return !!(dateTimeRegExp.exec(value)) // TODO && moment(value).isValid()
    },
    toMoment: function (value, timezone) {
      // TODO: add support for input like "YYYY-MM-dd h:mm:ss", where hour is missing a leading zero
      return moment.tz(value.length ? value.trim() : NaN, timezone)
    },
    fromMoment: function (moment, timezone) {
      return moment.clone().tz(timezone).format('YYYY-MM-DD HH:mm:ss')
    }
  },

  americanDateTime: {
    type: 'americanDateTime',
    category: 'human',
    isReadonly: false,
    hasTimezone: true,
    hasPeriod: false,
    label: (timezone) => `american date time\n${formatTimezone(timezone)}`,
    width: 200, // px
    isValid: function (value) {
      return !!(americanDateTimeRegExp.exec(value) && moment(value).isValid())
    },
    toMoment: function (value, timezone) {
      return moment.tz(value.length ? value.trim() : NaN, timezone)
    },
    fromMoment: function (moment, timezone) {
      return moment.clone().tz(timezone).format('MM/DD/YYYY hh:mm:ss A')
    }
  },

  russianDateTime: {
    type: 'russianDateTime',
    category: 'human',
    hasTimezone: true,
    isReadonly: false,
    hasPeriod: false,
    label: (timezone) => `russian date time\n${formatTimezone(timezone)}`,
    width: 180, // px
    isValid: function (value) {
      return !!(russianDateTimeRegExp.exec(value) && moment(value, 'DD.MM.YYYY HH:mm:ss').isValid())
    },
    toMoment: function (value, timezone) {
      return moment.tz(value.length ? value.trim() : NaN, 'DD.MM.YYYY HH:mm:ss', timezone)
    },
    fromMoment: function (moment, timezone) {
      return moment.clone().tz(timezone).format('DD.MM.YYYY HH:mm:ss')
    }
  },

  humanDate: {
    type: 'humanDate',
    category: 'human',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: false,
    label: (timezone) => `human date\n${formatTimezone(timezone)}`,
    width: 280, // px
    fromMoment: function (moment, timezone) {
      return moment.clone().tz(timezone).toString()
    }
  },

  weekday: {
    type: 'weekday',
    category: 'human',
    isReadonly: true,
    hasTimezone: true,
    hasPeriod: false,
    label: (timezone) => `weekday\n${formatTimezone(timezone)}`,
    width: 120, // px
    fromMoment: function (moment, timezone) {
      return moment.clone().tz(timezone).format('dddd')
    }
  }
}



// WEBPACK FOOTER //
// ./src/tttime/tttime.js